<script setup lang="ts">
import { faCircleQuestion } from '@fortawesome/pro-regular-svg-icons';
import PriceCard from './PriceCard.vue';
import HotelPriceClass from '@itinerary/hotel/HotelPriceClass';
import type HotelRoomType from '@itinerary/hotel/HotelRoomType';
import type HotelPackage from '@itinerary/hotel/HotelPackage';

type Option = {
    id: string;
    title: string;
};

interface IProps {
    item: HotelRoomType;
    nights: number;
    hideReserve?: boolean;
}
const { item, nights, hideReserve } = defineProps<IProps>();
const emit = defineEmits<{
    select: [];
}>();
const container = ref<HTMLDivElement>();
const needExpansion = ref(false);
const expanded = ref(false);
const packageSelectStore = usePackageSelectStore();
const title = computed(() => item.title || '');
const options = computed<Option[]>(() => {
    const doubleBedCount = item.roomConfigs[0]?.doubleBedCount;
    return [
        {
            id: 'capacity',
            title: `${item.capacity} نفره`,
        },
        {
            id: 'double-bed',
            title: `${doubleBedCount} تخت دابل`,
            condition: !!doubleBedCount,
        },
        {
            id: 'extra-bed',
            title: 'تخت اضافه',
            condition: item.hasExtraBed,
        },
    ].filter(item => item.condition !== false);
});
const emptyPrice = computed(
    () =>
        new HotelPriceClass(
            {
                availableRoomCount: 0,
                totalPrice: 0,
            },
            {
                hasExtraBed: item.hasExtraBed,
                extraBedCapacity: 0,
                checkoutHour: '',
                checkinHour: '',
                uniqueId: '',
                onlyThisClassSelectable: false,
            }
        )
);

const hotelPackage = computed(
    () => packageSelectStore.selectedPackages[0] as HotelPackage
);

const classes = computed(() => {
    if (
        hotelPackage.value.selectedPricesWithPriceClass &&
        hotelPackage.value.selectedPricesWithPriceClass[0] &&
        hotelPackage.value.selectedPricesWithPriceClass[0][1]! === item &&
        hotelPackage.value.selectedPricesWithPriceClass[0][0]!
            .onlyThisClassSelectable
    ) {
        return item.classes.filter(
            c => c.id === hotelPackage.value.selectedPrices![0].id
        );
    } else {
        return item.classes;
    }
});
onMounted(() => {
    needExpansion.value =
        container.value?.clientHeight !== container.value?.scrollHeight;
});
</script>

<template>
    <div class="room-card-container">
        <mr-blur-hash-image
            v-if="item.image"
            :src="
                item.image.original ||
                item.image.large ||
                item.image.small ||
                undefined
            "
            :hash="item.image.blurhash ?? undefined"
            :height="120"
            border-radius="8px"
            class="room-image-mobile"
            image-fit="cover"
        />
        <div class="header">
            <slot name="header-start" />
            <mr-typography
                type="span"
                :size="{ desktop: 'x-large', mobile: 'large' }"
                weight="700"
                grow
                class="title"
            >
                {{ title }}
                {{
                    classes[0]?.providerName
                        ? ` - (${classes[0]?.providerName})`
                        : ''
                }}
                <mr-tooltip v-if="item.description" :horizontal-alignment="100">
                    <template #tip>
                        <mr-icon
                            color="var(--blue-400)"
                            :icon="faCircleQuestion"
                        />
                    </template>
                    <template #detail>
                        <mr-typography>
                            {{ item.description }}
                        </mr-typography>
                    </template>
                </mr-tooltip>
            </mr-typography>

            <div class="options-list">
                <mr-typography
                    v-for="option in options"
                    :key="option.id"
                    weight="500"
                    color="var(--cool-gray-600)"
                    class="option"
                >
                    {{ option.title }}
                </mr-typography>
            </div>
        </div>
        <mr-divider
            v-if="!hideReserve"
            thickness="1px"
            class="mobile-divider"
        />
        <div class="body">
            <mr-blur-hash-image
                v-if="item.image"
                :src="
                    item.image.original ||
                    item.image.large ||
                    item.image.small ||
                    undefined
                "
                :hash="item.image.blurhash ?? undefined"
                :width="160"
                :height="100"
                border-radius="8px"
                class="room-image-desktop"
                image-fit="cover"
            />
            <div class="prices">
                <template v-if="item.classes.length">
                    <PriceCard
                        v-for="price in classes"
                        :key="price.id ?? undefined"
                        :item="price"
                        :nights
                        class="price"
                        :floating="hotelPackage.floatingMode"
                        :hide-reserve
                        @select="emit('select')"
                    >
                        <template #reserve>
                            <slot name="reserve" />
                        </template>
                    </PriceCard>
                </template>
                <PriceCard
                    v-else
                    :floating="hotelPackage.floatingMode"
                    :item="emptyPrice"
                    :nights
                    @select="emit('select')"
                />
                <template v-if="item.roomAmenities.length">
                    <mr-divider thickness="1px" class="amenities-divider" />
                    <div
                        ref="container"
                        class="amenities-container"
                        :class="{ expanded }"
                    >
                        <template
                            v-for="(amenity, index) in item.roomAmenities"
                            :key="amenity + index"
                        >
                            <mr-typography
                                size="small"
                                weight="500"
                                color="var(--cool-gray-600)"
                                white-space="nowrap"
                                class="amenity"
                            >
                                {{ amenity }}
                            </mr-typography>
                            <mr-divider
                                v-if="index !== item.roomAmenities.length - 1"
                                thickness="1px"
                                vertical
                                class="amenity-divider"
                            />
                        </template>
                        <mr-button
                            v-if="needExpansion"
                            size="small"
                            variant="transparent"
                            class="expand"
                            :class="{ expanded }"
                            @click="expanded = !expanded"
                        >
                            {{ expanded ? 'کمتر' : 'بیشتر' }}
                        </mr-button>
                    </div>
                </template>
            </div>
        </div>
    </div>
</template>

<style lang="scss" scoped>
.room-card-container {
    background-color: white;
    @include shadow();
    border-radius: 12px;
    padding: 12px;
    @include mobile-only {
        padding: 0;
        border-radius: 8px;
    }
    .room-image-mobile {
        @include desktop-only {
            display: none;
        }
        width: 100%;
        &:deep() {
            canvas {
                border-top-left-radius: 8px;
                border-top-right-radius: 8px;
            }
        }
    }

    .header {
        display: flex;
        align-items: center;
        column-gap: 6px;
        margin-bottom: 8px;
        @include mobile-only {
            border-bottom: none;
            padding-inline: 16px;
            flex-wrap: wrap;
            margin-top: 12px;
            margin-bottom: 0;
        }
        .title {
            display: flex;
            align-items: center;
            gap: 8px;
        }
        .options-list {
            display: flex;
            align-items: center;
            column-gap: 6px;
            @include mobile-only {
                flex-basis: 100%;
                max-width: 100%;
            }
            .option {
                display: flex;
                align-items: center;
                gap: 6px;
                &:not(:last-child) {
                    &:after {
                        content: ' ';
                        display: block;
                        border-left: 1px solid usePalette($p-cool-gray, $p-300);
                        height: 16px;
                    }
                }
            }
        }
    }
    .mobile-divider {
        @include desktop-only {
            display: none;
        }
        margin: 8px;
    }
    .body {
        display: flex;
        gap: 12px;
        .room-image-desktop {
            flex-shrink: 0;
            @include mobile-only {
                display: none;
            }
            &:deep() {
                canvas {
                    border-radius: 8px;
                }
            }
        }
        .prices {
            display: flex;
            flex-direction: column;
            align-items: stretch;
            flex-grow: 1;
            @include mobile-only {
                padding-bottom: 12px;
            }
            .price {
                margin-bottom: 8px;
                &:last-child {
                    margin-bottom: 0;
                }
            }
            .amenities-divider {
                margin: 4px;
                @include mobile-only {
                    display: none;
                }
            }
            .amenities-container {
                position: relative;
                display: flex;
                flex-wrap: wrap;
                align-items: center;
                justify-content: space-between;
                column-gap: 8px;
                max-height: 24px;
                overflow: hidden;
                transition: max-height ease 200ms;
                @include mobile-only {
                    padding-inline: 16px;
                }
                &.expanded {
                    max-height: 200px;
                }
                &:after {
                    content: '';
                    flex-grow: 1;
                }
                .amenity {
                    display: flex;
                    align-items: center;
                    gap: 8px;
                }
                .amenity-divider {
                    height: 12px;
                }
                .expand {
                    background-color: white;
                    position: absolute;
                    left: 0;
                    @include mobile-only {
                        left: 15px;
                    }
                    &.expanded {
                        position: relative;
                        left: auto;
                    }
                }
            }
        }
    }
}
</style>
